// ============================================
// INVENTARIO.JS — Módulo de inventario con pestañas
// Herramientas Beep, Equipos para instalar, Instalado en clientes
// Escáner de códigos, validación de series, asignaciones
// ============================================

import React, { useState, useMemo } from 'react';
import { BadgeFolio, generarFolio } from './utils/folios';
import { BarraBusqueda, FiltrosRapidos, ContadorResultados, HeaderOrdenable, useOrden, ordenarRegistros, BarraPestanas } from './utils/filtros';
import Escaner from './components/Escaner';

const colores = { principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#5e666e', amarillo: '#dcde74', verde: '#5cd4b0', rojo: '#e05c5c', fondo: '#f3f4f4' };
const PP = "'Poppins', sans-serif";

const estatusHerramientaColor = {
  'Disponible':     { bg: '#e8faf5', color: '#1D9E75' },
  'Asignada':       { bg: '#fff3e8', color: '#b55e1a' },
  'En reparación':  { bg: '#fde8e8', color: '#b02020' },
};

const estatusEquipoColor = {
  'En almacén':          { bg: '#f3effe', color: '#6d3fcc' },
  'Asignado a proyecto': { bg: '#e6f1fb', color: colores.oscuro },
  'Instalado':           { bg: '#e8faf5', color: '#1D9E75' },
};

// ============================================
// DATOS — Herramientas Beep (8)
// ============================================
const herramientasData = [
  { id: 1, folio: 'HER-2026-001', nombre: 'Taladro Percutor', marca: 'DeWalt', modelo: 'DCD996', numero_serie: 'DW-2024-00112', categoria: 'Herramienta', estatus: 'Asignada', responsable: 'Miguel G.', fecha_asignacion: '2026-03-24', historial_asignaciones: [{ responsable: 'Pedro R.', desde: '2026-01-10', hasta: '2026-02-15' }, { responsable: 'Miguel G.', desde: '2026-03-24', hasta: null }] },
  { id: 2, folio: 'HER-2026-002', nombre: 'Rotomartillo SDS', marca: 'Bosch', modelo: 'GBH 2-26', numero_serie: 'BSH-2024-00234', categoria: 'Herramienta', estatus: 'Disponible', responsable: null, fecha_asignacion: null, historial_asignaciones: [{ responsable: 'Miguel G.', desde: '2025-11-01', hasta: '2026-01-20' }] },
  { id: 3, folio: 'HER-2026-003', nombre: 'Multímetro Digital', marca: 'Fluke', modelo: '117', numero_serie: 'FLK-2023-00891', categoria: 'Herramienta', estatus: 'Asignada', responsable: 'Pedro R.', fecha_asignacion: '2026-03-10', historial_asignaciones: [{ responsable: 'Pedro R.', desde: '2026-03-10', hasta: null }] },
  { id: 4, folio: 'HER-2026-004', nombre: 'Ponchadora RJ45', marca: 'Klein Tools', modelo: 'VDV226-110', numero_serie: 'KLN-2024-00456', categoria: 'Herramienta', estatus: 'Disponible', responsable: null, fecha_asignacion: null, historial_asignaciones: [] },
  { id: 5, folio: 'HER-2026-005', nombre: 'Escalera Telescópica 4m', marca: 'Werner', modelo: 'MT-22', numero_serie: 'WRN-2023-00789', categoria: 'Herramienta', estatus: 'Asignada', responsable: 'Miguel G.', fecha_asignacion: '2026-03-20', historial_asignaciones: [{ responsable: 'Miguel G.', desde: '2026-03-20', hasta: null }] },
  { id: 6, folio: 'HER-2026-006', nombre: 'Probador de Cable', marca: 'Fluke', modelo: 'MicroScanner2', numero_serie: 'FLK-2024-01100', categoria: 'Herramienta', estatus: 'En reparación', responsable: null, fecha_asignacion: null, historial_asignaciones: [{ responsable: 'Pedro R.', desde: '2025-08-15', hasta: '2026-02-28' }] },
  { id: 7, folio: 'HER-2026-007', nombre: 'Kit Destornilladores Precisión', marca: 'Wiha', modelo: '75965', numero_serie: 'WHA-2024-00330', categoria: 'Herramienta', estatus: 'Asignada', responsable: 'Carlos L.', fecha_asignacion: '2026-03-25', historial_asignaciones: [{ responsable: 'Carlos L.', desde: '2026-03-25', hasta: null }] },
  { id: 8, folio: 'HER-2026-008', nombre: 'Peladora de Cable Coaxial', marca: 'Klein Tools', modelo: 'VDV110-261', numero_serie: 'KLN-2024-00789', categoria: 'Herramienta', estatus: 'Disponible', responsable: null, fecha_asignacion: null, historial_asignaciones: [{ responsable: 'Miguel G.', desde: '2025-12-01', hasta: '2026-01-31' }] },
];

// ============================================
// DATOS — Equipos para instalación (12)
// ============================================
const equiposInstalacionData = [
  { id: 1, folio: 'EQP-2026-001', nombre: 'Cámara Domo IP 4MP', marca: 'Hikvision', modelo: 'DS-2CD2145', numero_serie: 'HIK-2026-30001', categoria: 'Cámara', estatus: 'En almacén', proyecto_asignado: null, sucursal_id: null, tecnico_instalacion: null, fecha_instalacion: null },
  { id: 2, folio: 'EQP-2026-002', nombre: 'Cámara Bullet 2MP', marca: 'Dahua', modelo: 'DH-HAC-HFW1200', numero_serie: 'DAH-2026-30010', categoria: 'Cámara', estatus: 'Asignado a proyecto', proyecto_asignado: 'PRY-2026-001 Bodega Central', sucursal_id: 1, tecnico_instalacion: 'Miguel G.', fecha_instalacion: null },
  { id: 3, folio: 'EQP-2026-003', nombre: 'NVR 32CH 4K', marca: 'Hikvision', modelo: 'DS-7732NI-K4', numero_serie: 'HIK-2026-30020', categoria: 'NVR', estatus: 'Asignado a proyecto', proyecto_asignado: 'PRY-2026-001 Bodega Central', sucursal_id: 1, tecnico_instalacion: 'Miguel G.', fecha_instalacion: null },
  { id: 4, folio: 'EQP-2026-004', nombre: 'Switch PoE 24P', marca: 'TP-Link', modelo: 'TL-SG1024PE', numero_serie: 'TPL-2026-30030', categoria: 'Switch', estatus: 'En almacén', proyecto_asignado: null, sucursal_id: null, tecnico_instalacion: null, fecha_instalacion: null },
  { id: 5, folio: 'EQP-2026-005', nombre: 'Cámara PTZ 4MP', marca: 'Hikvision', modelo: 'DS-2DE4425IW', numero_serie: 'HIK-2026-30040', categoria: 'Cámara', estatus: 'Asignado a proyecto', proyecto_asignado: 'PRY-2026-002 Farma Polanco', sucursal_id: 6, tecnico_instalacion: 'Pedro R.', fecha_instalacion: null },
  { id: 6, folio: 'EQP-2026-006', nombre: 'DVR 16CH Turbo', marca: 'Hikvision', modelo: 'DS-7216HUHI', numero_serie: 'HIK-2026-30050', categoria: 'DVR', estatus: 'En almacén', proyecto_asignado: null, sucursal_id: null, tecnico_instalacion: null, fecha_instalacion: null },
  { id: 7, folio: 'EQP-2026-007', nombre: 'Lector Facial', marca: 'ZKTeco', modelo: 'SpeedFace V5L', numero_serie: 'ZKT-2026-30060', categoria: 'Lector', estatus: 'Asignado a proyecto', proyecto_asignado: 'PRY-2026-001 Bodega Central', sucursal_id: 1, tecnico_instalacion: 'Miguel G.', fecha_instalacion: null },
  { id: 8, folio: 'EQP-2026-008', nombre: 'Panel Alarma 8 Zonas', marca: 'DSC', modelo: 'HS2032', numero_serie: 'DSC-2026-30070', categoria: 'Panel alarma', estatus: 'En almacén', proyecto_asignado: null, sucursal_id: null, tecnico_instalacion: null, fecha_instalacion: null },
  { id: 9, folio: 'EQP-2026-009', nombre: 'Sensor Movimiento', marca: 'DSC', modelo: 'LC-100-PI', numero_serie: 'DSC-2026-30080', categoria: 'Sensor', estatus: 'Asignado a proyecto', proyecto_asignado: 'PRY-2026-002 Farma Polanco', sucursal_id: 6, tecnico_instalacion: 'Pedro R.', fecha_instalacion: null },
  { id: 10, folio: 'EQP-2026-010', nombre: 'Cámara Domo IP 2MP', marca: 'Dahua', modelo: 'DH-IPC-HDW2231T', numero_serie: 'DAH-2026-30090', categoria: 'Cámara', estatus: 'En almacén', proyecto_asignado: null, sucursal_id: null, tecnico_instalacion: null, fecha_instalacion: null },
  { id: 11, folio: 'EQP-2026-011', nombre: 'Switch PoE 8P', marca: 'TP-Link', modelo: 'TL-SG108PE', numero_serie: 'TPL-2026-30100', categoria: 'Switch', estatus: 'Asignado a proyecto', proyecto_asignado: 'PRY-2026-002 Farma Polanco', sucursal_id: 6, tecnico_instalacion: 'Pedro R.', fecha_instalacion: null },
  { id: 12, folio: 'EQP-2026-012', nombre: 'Rack Gabinete 12U', marca: 'LinkedPro', modelo: 'LP-12U', numero_serie: 'LPR-2026-30110', categoria: 'Rack', estatus: 'En almacén', proyecto_asignado: null, sucursal_id: null, tecnico_instalacion: null, fecha_instalacion: null },
];

// Equipos ya instalados en clientes (reutilizamos estructura)
const equiposInstaladosData = [
  { id: 101, folio: 'EQP-2024-001', nombre: 'Cámara Domo Entrada', marca: 'Hikvision', modelo: 'DS-2CD2145', numero_serie: 'HIK-2021-15670', categoria: 'Cámara', estatus: 'Instalado', sucursal_id: 1, tecnico_instalacion: 'Miguel G.', fecha_instalacion: '2021-11-15' },
  { id: 102, folio: 'EQP-2024-002', nombre: 'DVR Principal 16CH', marca: 'Dahua', modelo: 'XVR5116HS', numero_serie: 'DAH-2017-00098', categoria: 'DVR', estatus: 'Instalado', sucursal_id: 1, tecnico_instalacion: 'Miguel G.', fecha_instalacion: '2017-03-15' },
  { id: 103, folio: 'EQP-2024-003', nombre: 'Switch PoE 24P', marca: 'TP-Link', modelo: 'TL-SG1024PE', numero_serie: 'TPL-2017-04521', categoria: 'Switch', estatus: 'Instalado', sucursal_id: 1, tecnico_instalacion: 'Miguel G.', fecha_instalacion: '2017-03-15' },
  { id: 104, folio: 'EQP-2024-005', nombre: 'Cámara PTZ Patio', marca: 'Hikvision', modelo: 'DS-2DE4425IW', numero_serie: 'HIK-2018-07901', categoria: 'Cámara', estatus: 'Instalado', sucursal_id: 1, tecnico_instalacion: 'Pedro R.', fecha_instalacion: '2018-08-22' },
  { id: 105, folio: 'EQP-2024-006', nombre: 'Lector Biométrico Entrada', marca: 'ZKTeco', modelo: 'SpeedFace V5L', numero_serie: 'ZKT-2019-01123', categoria: 'Lector', estatus: 'Instalado', sucursal_id: 1, tecnico_instalacion: 'Miguel G.', fecha_instalacion: '2019-04-10' },
  { id: 106, folio: 'EQP-2024-009', nombre: 'Cámara Domo Almacén', marca: 'Dahua', modelo: 'DH-HAC-HDW1200', numero_serie: 'DAH-2019-03310', categoria: 'Cámara', estatus: 'Instalado', sucursal_id: 2, tecnico_instalacion: 'Pedro R.', fecha_instalacion: '2019-11-05' },
  { id: 107, folio: 'EQP-2024-011', nombre: 'Switch PoE 8P Norte', marca: 'TP-Link', modelo: 'TL-SG108PE', numero_serie: 'TPL-2019-08901', categoria: 'Switch', estatus: 'Instalado', sucursal_id: 2, tecnico_instalacion: 'Pedro R.', fecha_instalacion: '2019-11-05' },
  { id: 108, folio: 'EQP-2024-013', nombre: 'Cámara Domo Recepción', marca: 'Hikvision', modelo: 'DS-2CD2145', numero_serie: 'HIK-2022-18440', categoria: 'Cámara', estatus: 'Instalado', sucursal_id: 3, tecnico_instalacion: 'Miguel G.', fecha_instalacion: '2022-05-18' },
  { id: 109, folio: 'EQP-2024-015', nombre: 'Lector Acceso Corporativo', marca: 'ZKTeco', modelo: 'SpeedFace V5L', numero_serie: 'ZKT-2024-04501', categoria: 'Lector', estatus: 'Instalado', sucursal_id: 4, tecnico_instalacion: 'Pedro R.', fecha_instalacion: '2024-01-20' },
  { id: 110, folio: 'EQP-2024-016', nombre: 'Cámara Domo Mostrador', marca: 'Dahua', modelo: 'DH-HAC-HDW1200', numero_serie: 'DAH-2020-05501', categoria: 'Cámara', estatus: 'Instalado', sucursal_id: 5, tecnico_instalacion: 'Pedro R.', fecha_instalacion: '2020-09-12' },
  { id: 111, folio: 'EQP-2024-017', nombre: 'Cámara Bullet Fachada', marca: 'Hikvision', modelo: 'DS-2CE16D0T', numero_serie: 'HIK-2020-12340', categoria: 'Cámara', estatus: 'Instalado', sucursal_id: 5, tecnico_instalacion: 'Pedro R.', fecha_instalacion: '2020-09-12' },
  { id: 112, folio: 'EQP-2024-020', nombre: 'Cámara Domo Farmacia Pol.', marca: 'Dahua', modelo: 'DH-HAC-HDW1200', numero_serie: 'DAH-2021-06601', categoria: 'Cámara', estatus: 'Instalado', sucursal_id: 6, tecnico_instalacion: 'Pedro R.', fecha_instalacion: '2021-02-10' },
];

// Grupos, razones y sucursales
const gruposCliente = [
  { id: 1, nombre: 'Bodegas Alianza' },
  { id: 2, nombre: 'Farma Plus' },
];
const razonesCliente = [
  { id: 1, grupo_id: 1, nombre: 'Bodegas Alianza SA de CV' },
  { id: 2, grupo_id: 1, nombre: 'Alianza Logística del Bajío SA de CV' },
  { id: 3, grupo_id: 2, nombre: 'Farma Plus SA de CV' },
];
const sucursalesCliente = [
  { id: 0, razon_id: 0, nombre: 'Almacén Beep', ciudad: 'CDMX' },
  { id: 1, razon_id: 1, nombre: 'Bodega Central CDMX', ciudad: 'CDMX' },
  { id: 2, razon_id: 1, nombre: 'Bodega Norte MTY', ciudad: 'Monterrey' },
  { id: 3, razon_id: 2, nombre: 'Bodega Sur CDMX', ciudad: 'CDMX' },
  { id: 4, razon_id: 2, nombre: 'Oficina Corporativa Reforma', ciudad: 'CDMX' },
  { id: 5, razon_id: 3, nombre: 'Farma Plus Matriz', ciudad: 'CDMX' },
  { id: 6, razon_id: 3, nombre: 'Farma Plus Polanco', ciudad: 'CDMX' },
];

// Helpers
function getSucursal(id) { return sucursalesCliente.find(s => s.id === id); }
function getRazon(id) { return razonesCliente.find(r => r.id === id); }
function getGrupo(id) { return gruposCliente.find(g => g.id === id); }
function diasDesde(fecha) {
  if (!fecha) return 0;
  const d = new Date(fecha);
  const hoy = new Date();
  return Math.floor((hoy - d) / (1000 * 60 * 60 * 24));
}

function BadgeEstatus({ estatus, mapa }) {
  const cfg = mapa[estatus] || { bg: '#f3f4f4', color: '#4e5357' };
  return <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: cfg.bg, color: cfg.color, fontFamily: PP }}>{estatus}</span>;
}

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function Inventario() {
  const [pestana, setPestana] = useState('herramientas');
  const [busqH, setBusqH] = useState('');
  const [filtroH, setFiltroH] = useState('');
  const [busqE, setBusqE] = useState('');
  const [filtroE, setFiltroE] = useState('');
  const [filtroProyE, setFiltroProyE] = useState('');
  const [busqI, setBusqI] = useState('');
  const [filtroGrupoI, setFiltroGrupoI] = useState('');
  const [agruparPorProyecto, setAgruparPorProyecto] = useState(false);
  const [ordenH, toggleOrdenH] = useOrden('nombre');
  const [ordenE, toggleOrdenE] = useOrden('nombre');
  const [ordenI, toggleOrdenI] = useOrden('sucursal_id');
  const [escanerAbierto, setEscanerAbierto] = useState(false);
  const [resultadoEscaneo, setResultadoEscaneo] = useState(null);
  const [mostrarHistorial, setMostrarHistorial] = useState(null);
  const [formNuevo, setFormNuevo] = useState(null);
  const [errorSerie, setErrorSerie] = useState('');
  const esMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const tabs = [
    { key: 'herramientas', label: 'Herramientas Beep' },
    { key: 'equipos', label: 'Equipos para instalar' },
    { key: 'instalado', label: 'Instalado en clientes' },
  ];

  // --- Herramientas filtradas ---
  const herramientasFiltradas = useMemo(() => {
    let r = [...herramientasData];
    if (busqH) {
      const q = busqH.toLowerCase();
      r = r.filter(h => h.nombre.toLowerCase().includes(q) || h.marca.toLowerCase().includes(q) || h.numero_serie.toLowerCase().includes(q) || (h.responsable || '').toLowerCase().includes(q));
    }
    if (filtroH) r = r.filter(h => h.estatus === filtroH);
    return ordenarRegistros(r, ordenH.campo, ordenH.dir);
  }, [busqH, filtroH, ordenH]);

  const contHerr = useMemo(() => ({
    Disponible: herramientasData.filter(h => h.estatus === 'Disponible').length,
    Asignada: herramientasData.filter(h => h.estatus === 'Asignada').length,
    'En reparación': herramientasData.filter(h => h.estatus === 'En reparación').length,
  }), []);

  // --- Equipos para instalar filtrados ---
  const equiposFiltrados = useMemo(() => {
    let r = [...equiposInstalacionData];
    if (busqE) {
      const q = busqE.toLowerCase();
      r = r.filter(e => e.nombre.toLowerCase().includes(q) || e.marca.toLowerCase().includes(q) || e.numero_serie.toLowerCase().includes(q) || e.categoria.toLowerCase().includes(q));
    }
    if (filtroE) r = r.filter(e => e.estatus === filtroE);
    if (filtroProyE) r = r.filter(e => (e.proyecto_asignado || '').includes(filtroProyE));
    return ordenarRegistros(r, ordenE.campo, ordenE.dir);
  }, [busqE, filtroE, filtroProyE, ordenE]);

  const contEquip = useMemo(() => ({
    'En almacén': equiposInstalacionData.filter(e => e.estatus === 'En almacén').length,
    'Asignado a proyecto': equiposInstalacionData.filter(e => e.estatus === 'Asignado a proyecto').length,
  }), []);

  const proyectosUnicos = useMemo(() => {
    const set = new Set();
    equiposInstalacionData.forEach(e => { if (e.proyecto_asignado) set.add(e.proyecto_asignado); });
    return Array.from(set);
  }, []);

  // --- Instalado en clientes filtrados ---
  const instaladosFiltrados = useMemo(() => {
    let r = [...equiposInstaladosData];
    if (busqI) {
      const q = busqI.toLowerCase();
      r = r.filter(e => e.nombre.toLowerCase().includes(q) || e.marca.toLowerCase().includes(q) || e.numero_serie.toLowerCase().includes(q));
    }
    if (filtroGrupoI) {
      const grupoId = parseInt(filtroGrupoI);
      const razonesDelGrupo = razonesCliente.filter(rz => rz.grupo_id === grupoId).map(rz => rz.id);
      const sucsDelGrupo = sucursalesCliente.filter(s => razonesDelGrupo.includes(s.razon_id)).map(s => s.id);
      r = r.filter(e => sucsDelGrupo.includes(e.sucursal_id));
    }
    return ordenarRegistros(r, ordenI.campo, ordenI.dir);
  }, [busqI, filtroGrupoI, ordenI]);

  // Agrupación de instalados por grupo > sucursal
  const instaladosAgrupados = useMemo(() => {
    const grupos = {};
    instaladosFiltrados.forEach(eq => {
      const suc = getSucursal(eq.sucursal_id);
      if (!suc) return;
      const razon = getRazon(suc.razon_id);
      if (!razon) return;
      const grupo = getGrupo(razon.grupo_id);
      if (!grupo) return;
      const gKey = grupo.nombre;
      const sKey = suc.nombre;
      if (!grupos[gKey]) grupos[gKey] = {};
      if (!grupos[gKey][sKey]) grupos[gKey][sKey] = [];
      grupos[gKey][sKey].push(eq);
    });
    return grupos;
  }, [instaladosFiltrados]);

  // --- Escáner ---
  const handleEscaneo = (codigo) => {
    setEscanerAbierto(false);
    const enHerr = herramientasData.find(h => h.numero_serie === codigo);
    if (enHerr) { setResultadoEscaneo({ tipo: 'herramienta', item: enHerr }); return; }
    const enEquip = equiposInstalacionData.find(e => e.numero_serie === codigo);
    if (enEquip) { setResultadoEscaneo({ tipo: 'equipo', item: enEquip }); return; }
    const enInstalado = equiposInstaladosData.find(e => e.numero_serie === codigo);
    if (enInstalado) { setResultadoEscaneo({ tipo: 'instalado', item: enInstalado }); return; }
    setResultadoEscaneo({ tipo: 'no_encontrado', codigo });
    setFormNuevo({ numero_serie: codigo, nombre: '', marca: '', modelo: '', categoria: 'Cámara' });
  };

  const validarSerieNueva = (serie) => {
    const enH = herramientasData.find(h => h.numero_serie === serie);
    if (enH) return 'Ya existe en Herramientas: ' + enH.nombre + ' (' + enH.folio + ')';
    const enE = equiposInstalacionData.find(e => e.numero_serie === serie);
    if (enE) return 'Ya existe en Equipos: ' + enE.nombre + ' (' + enE.folio + ')';
    const enI = equiposInstaladosData.find(e => e.numero_serie === serie);
    if (enI) return 'Ya existe en Instalados: ' + enI.nombre + ' (' + enI.folio + ')';
    return '';
  };

  const handleNuevoSerieChange = (val) => {
    setFormNuevo(p => ({ ...p, numero_serie: val }));
    setErrorSerie(validarSerieNueva(val));
  };

  // Card style
  const cardStyle = { background: '#fff', borderRadius: '12px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', fontFamily: PP };
  const labelStyle = { fontSize: '10px', color: colores.grisClaro, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: '600' };
  const valStyle = { fontSize: '13px', color: colores.gris, fontWeight: '500' };
  const btnStyle = (bg) => ({ padding: '7px 14px', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: PP, background: bg || colores.oscuro, color: '#fff' });

  // ============================================
  // RENDER — Herramientas
  // ============================================
  const renderHerramientas = () => {
    const opsFiltro = [
      { value: '', label: 'Todas', count: herramientasData.length },
      { value: 'Disponible', label: 'Disponibles', count: contHerr.Disponible },
      { value: 'Asignada', label: 'Asignadas', count: contHerr.Asignada },
      { value: 'En reparación', label: 'En reparación', count: contHerr['En reparación'] },
    ];
    return (
      <div>
        <BarraBusqueda value={busqH} onChange={setBusqH} placeholder="Buscar herramienta, marca, serie, responsable..." />
        <FiltrosRapidos opciones={opsFiltro} activo={filtroH} onChange={setFiltroH} />
        <ContadorResultados total={herramientasData.length} filtrados={herramientasFiltradas.length} />
        {esMobile ? herramientasFiltradas.map(h => {
          const diasAsig = diasDesde(h.fecha_asignacion);
          const alertaRojo = h.estatus === 'Asignada' && diasAsig > 3;
          return (
            <div key={h.id} style={{ ...cardStyle, borderLeft: alertaRojo ? '4px solid ' + colores.rojo : '4px solid transparent' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <BadgeFolio folio={h.folio} />
                <BadgeEstatus estatus={h.estatus} mapa={estatusHerramientaColor} />
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris, marginBottom: '4px' }}>{h.nombre}</div>
              <div style={{ fontSize: '12px', color: colores.grisClaro }}>{h.marca} {h.modelo}</div>
              <div style={{ fontSize: '11px', color: colores.grisClaro, marginTop: '4px' }}>Serie: {h.numero_serie}</div>
              {h.responsable && <div style={{ fontSize: '12px', color: colores.gris, marginTop: '6px' }}>Responsable: <strong>{h.responsable}</strong> {alertaRojo && <span style={{ color: colores.rojo, fontSize: '11px', fontWeight: '700' }}>({diasAsig} dias)</span>}</div>}
              <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                {h.estatus === 'Asignada' && <button style={btnStyle(colores.verde)} onClick={() => alert('Devolución registrada para ' + h.nombre)}>Registrar devolución</button>}
                <button style={btnStyle('#f3f4f4')} onClick={() => setMostrarHistorial(h.id)}><span style={{ color: colores.gris }}>Historial</span></button>
              </div>
            </div>
          );
        }) : (
          <div style={{ overflowX: 'auto', marginTop: '12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <HeaderOrdenable label="Folio" campo="folio" orden={ordenH} onOrdenar={toggleOrdenH} />
                  <HeaderOrdenable label="Herramienta" campo="nombre" orden={ordenH} onOrdenar={toggleOrdenH} />
                  <HeaderOrdenable label="Marca / Modelo" campo="marca" orden={ordenH} onOrdenar={toggleOrdenH} />
                  <HeaderOrdenable label="No. Serie" campo="numero_serie" orden={ordenH} onOrdenar={toggleOrdenH} />
                  <HeaderOrdenable label="Estatus" campo="estatus" orden={ordenH} onOrdenar={toggleOrdenH} />
                  <HeaderOrdenable label="Responsable" campo="responsable" orden={ordenH} onOrdenar={toggleOrdenH} />
                  <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '600', color: colores.grisClaro, textTransform: 'uppercase' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {herramientasFiltradas.map(h => {
                  const diasAsig = diasDesde(h.fecha_asignacion);
                  const alertaRojo = h.estatus === 'Asignada' && diasAsig > 3;
                  return (
                    <tr key={h.id} style={{ borderBottom: '1px solid #f0f0f0', background: alertaRojo ? '#fff5f5' : '#fff' }}>
                      <td style={{ padding: '10px 16px' }}><BadgeFolio folio={h.folio} /></td>
                      <td style={{ padding: '10px 16px', fontSize: '13px', fontWeight: '600', color: colores.gris }}>{h.nombre}</td>
                      <td style={{ padding: '10px 16px', fontSize: '12px', color: colores.grisClaro }}>{h.marca} {h.modelo}</td>
                      <td style={{ padding: '10px 16px', fontSize: '12px', color: colores.gris, fontFamily: 'monospace' }}>{h.numero_serie}</td>
                      <td style={{ padding: '10px 16px' }}><BadgeEstatus estatus={h.estatus} mapa={estatusHerramientaColor} /></td>
                      <td style={{ padding: '10px 16px', fontSize: '12px', color: colores.gris }}>
                        {h.responsable || <span style={{ color: '#ccc' }}>--</span>}
                        {alertaRojo && <span style={{ display: 'block', fontSize: '10px', color: colores.rojo, fontWeight: '700' }}>{diasAsig} dias asignada</span>}
                      </td>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {h.estatus === 'Asignada' && <button style={btnStyle(colores.verde)} onClick={() => alert('Devolución registrada para ' + h.nombre)}>Devolver</button>}
                          <button style={{ ...btnStyle('#f3f4f4'), color: colores.gris }} onClick={() => setMostrarHistorial(h.id)}>Historial</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // RENDER — Equipos para instalar
  // ============================================
  const renderEquipos = () => {
    const opsFiltro = [
      { value: '', label: 'Todos', count: equiposInstalacionData.length },
      { value: 'En almacén', label: 'En almacén', count: contEquip['En almacén'] },
      { value: 'Asignado a proyecto', label: 'Asignado a proyecto', count: contEquip['Asignado a proyecto'] },
    ];

    const renderLista = (lista) => esMobile ? lista.map(e => (
      <div key={e.id} style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <BadgeFolio folio={e.folio} />
          <BadgeEstatus estatus={e.estatus} mapa={estatusEquipoColor} />
        </div>
        <div style={{ fontSize: '14px', fontWeight: '600', color: colores.gris, marginBottom: '4px' }}>{e.nombre}</div>
        <div style={{ fontSize: '12px', color: colores.grisClaro }}>{e.marca} {e.modelo} | {e.categoria}</div>
        <div style={{ fontSize: '11px', color: colores.grisClaro, marginTop: '4px' }}>Serie: {e.numero_serie}</div>
        {e.proyecto_asignado && <div style={{ fontSize: '12px', color: colores.oscuro, marginTop: '6px', fontWeight: '500' }}>{e.proyecto_asignado}</div>}
        {e.tecnico_instalacion && <div style={{ fontSize: '11px', color: colores.grisClaro, marginTop: '2px' }}>Técnico: {e.tecnico_instalacion}</div>}
      </div>
    )) : (
      <div style={{ overflowX: 'auto', marginTop: '12px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
              <HeaderOrdenable label="Folio" campo="folio" orden={ordenE} onOrdenar={toggleOrdenE} />
              <HeaderOrdenable label="Equipo" campo="nombre" orden={ordenE} onOrdenar={toggleOrdenE} />
              <HeaderOrdenable label="Categoría" campo="categoria" orden={ordenE} onOrdenar={toggleOrdenE} />
              <HeaderOrdenable label="Marca / Modelo" campo="marca" orden={ordenE} onOrdenar={toggleOrdenE} />
              <HeaderOrdenable label="No. Serie" campo="numero_serie" orden={ordenE} onOrdenar={toggleOrdenE} />
              <HeaderOrdenable label="Estatus" campo="estatus" orden={ordenE} onOrdenar={toggleOrdenE} />
              <HeaderOrdenable label="Proyecto" campo="proyecto_asignado" orden={ordenE} onOrdenar={toggleOrdenE} />
              <th style={{ padding: '12px 16px', fontSize: '11px', fontWeight: '600', color: colores.grisClaro, textTransform: 'uppercase' }}>Técnico</th>
            </tr>
          </thead>
          <tbody>
            {lista.map(e => (
              <tr key={e.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '10px 16px' }}><BadgeFolio folio={e.folio} /></td>
                <td style={{ padding: '10px 16px', fontSize: '13px', fontWeight: '600', color: colores.gris }}>{e.nombre}</td>
                <td style={{ padding: '10px 16px', fontSize: '12px', color: colores.grisClaro }}>{e.categoria}</td>
                <td style={{ padding: '10px 16px', fontSize: '12px', color: colores.grisClaro }}>{e.marca} {e.modelo}</td>
                <td style={{ padding: '10px 16px', fontSize: '12px', color: colores.gris, fontFamily: 'monospace' }}>{e.numero_serie}</td>
                <td style={{ padding: '10px 16px' }}><BadgeEstatus estatus={e.estatus} mapa={estatusEquipoColor} /></td>
                <td style={{ padding: '10px 16px', fontSize: '12px', color: colores.oscuro, fontWeight: '500' }}>{e.proyecto_asignado || <span style={{ color: '#ccc' }}>--</span>}</td>
                <td style={{ padding: '10px 16px', fontSize: '12px', color: colores.gris }}>{e.tecnico_instalacion || <span style={{ color: '#ccc' }}>--</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );

    // Agrupado por proyecto
    if (agruparPorProyecto) {
      const porProyecto = {};
      equiposFiltrados.forEach(e => {
        const key = e.proyecto_asignado || 'Sin proyecto asignado';
        if (!porProyecto[key]) porProyecto[key] = [];
        porProyecto[key].push(e);
      });
      return (
        <div>
          <BarraBusqueda value={busqE} onChange={setBusqE} placeholder="Buscar equipo, marca, serie, categoría..." />
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
            <FiltrosRapidos opciones={opsFiltro} activo={filtroE} onChange={setFiltroE} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <ContadorResultados total={equiposInstalacionData.length} filtrados={equiposFiltrados.length} />
            <button style={{ ...btnStyle('#f3f4f4'), color: colores.oscuro }} onClick={() => setAgruparPorProyecto(false)}>Vista lista</button>
          </div>
          {Object.entries(porProyecto).map(([proy, items]) => (
            <div key={proy} style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: colores.oscuro, padding: '8px 12px', background: '#e6f1fb', borderRadius: '8px', marginBottom: '8px' }}>{proy} <span style={{ fontWeight: '400', fontSize: '11px', color: colores.grisClaro }}>({items.length} equipos)</span></div>
              {renderLista(items)}
            </div>
          ))}
        </div>
      );
    }

    return (
      <div>
        <BarraBusqueda value={busqE} onChange={setBusqE} placeholder="Buscar equipo, marca, serie, categoría..." />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
          <FiltrosRapidos opciones={opsFiltro} activo={filtroE} onChange={setFiltroE} />
          {proyectosUnicos.length > 0 && (
            <select value={filtroProyE} onChange={e => setFiltroProyE(e.target.value)} style={{ padding: '5px 10px', borderRadius: '8px', border: '1px solid #e0e0e0', fontSize: '12px', fontFamily: PP, color: colores.gris, background: '#fff' }}>
              <option value="">Todos los proyectos</option>
              {proyectosUnicos.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <ContadorResultados total={equiposInstalacionData.length} filtrados={equiposFiltrados.length} />
          <button style={{ ...btnStyle('#f3f4f4'), color: colores.oscuro }} onClick={() => setAgruparPorProyecto(true)}>Agrupar por proyecto</button>
        </div>
        {renderLista(equiposFiltrados)}
      </div>
    );
  };

  // ============================================
  // RENDER — Instalado en clientes
  // ============================================
  const renderInstalados = () => {
    const opsGrupo = [
      { value: '', label: 'Todos', count: equiposInstaladosData.length },
      ...gruposCliente.map(g => {
        const razonesIds = razonesCliente.filter(r => r.grupo_id === g.id).map(r => r.id);
        const sucIds = sucursalesCliente.filter(s => razonesIds.includes(s.razon_id)).map(s => s.id);
        const cnt = equiposInstaladosData.filter(e => sucIds.includes(e.sucursal_id)).length;
        return { value: String(g.id), label: g.nombre, count: cnt };
      }),
    ];
    return (
      <div>
        <BarraBusqueda value={busqI} onChange={setBusqI} placeholder="Buscar equipo instalado, marca, serie..." />
        <FiltrosRapidos opciones={opsGrupo} activo={filtroGrupoI} onChange={setFiltroGrupoI} />
        <ContadorResultados total={equiposInstaladosData.length} filtrados={instaladosFiltrados.length} />
        {Object.entries(instaladosAgrupados).map(([grupo, sucursales]) => (
          <div key={grupo} style={{ marginBottom: '24px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: colores.oscuro, marginBottom: '10px', paddingBottom: '6px', borderBottom: '2px solid ' + colores.principal }}>{grupo}</div>
            {Object.entries(sucursales).map(([sucNombre, equipos]) => (
              <div key={sucNombre} style={{ marginBottom: '14px', marginLeft: '12px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600', color: colores.gris, marginBottom: '8px', padding: '4px 10px', background: colores.fondo, borderRadius: '6px', display: 'inline-block' }}>{sucNombre} <span style={{ fontWeight: '400', color: colores.grisClaro }}>({equipos.length})</span></div>
                {esMobile ? equipos.map(e => (
                  <div key={e.id} style={cardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <BadgeFolio folio={e.folio} />
                      <span style={{ fontSize: '11px', color: colores.grisClaro }}>{e.fecha_instalacion}</span>
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris }}>{e.nombre}</div>
                    <div style={{ fontSize: '11px', color: colores.grisClaro }}>{e.marca} {e.modelo} | {e.categoria}</div>
                    <div style={{ fontSize: '11px', color: colores.grisClaro, marginTop: '2px' }}>Serie: {e.numero_serie}</div>
                    <div style={{ marginTop: '8px' }}>
                      <button style={{ ...btnStyle('#f3f4f4'), color: colores.oscuro, fontSize: '11px' }} onClick={() => alert('Navegar a cliente: ' + sucNombre)}>Ver en cliente</button>
                    </div>
                  </div>
                )) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: PP, marginBottom: '8px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid #e8e8e8' }}>
                        <HeaderOrdenable label="Folio" campo="folio" orden={ordenI} onOrdenar={toggleOrdenI} />
                        <HeaderOrdenable label="Equipo" campo="nombre" orden={ordenI} onOrdenar={toggleOrdenI} />
                        <HeaderOrdenable label="Categoría" campo="categoria" orden={ordenI} onOrdenar={toggleOrdenI} />
                        <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: '600', color: colores.grisClaro, textTransform: 'uppercase', textAlign: 'left' }}>Marca / Modelo</th>
                        <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: '600', color: colores.grisClaro, textTransform: 'uppercase', textAlign: 'left' }}>No. Serie</th>
                        <HeaderOrdenable label="Instalación" campo="fecha_instalacion" orden={ordenI} onOrdenar={toggleOrdenI} />
                        <th style={{ padding: '10px 16px', fontSize: '11px', fontWeight: '600', color: colores.grisClaro, textTransform: 'uppercase', textAlign: 'left' }}>Acción</th>
                      </tr>
                    </thead>
                    <tbody>
                      {equipos.map(e => (
                        <tr key={e.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 16px' }}><BadgeFolio folio={e.folio} /></td>
                          <td style={{ padding: '8px 16px', fontSize: '13px', fontWeight: '600', color: colores.gris }}>{e.nombre}</td>
                          <td style={{ padding: '8px 16px', fontSize: '12px', color: colores.grisClaro }}>{e.categoria}</td>
                          <td style={{ padding: '8px 16px', fontSize: '12px', color: colores.grisClaro }}>{e.marca} {e.modelo}</td>
                          <td style={{ padding: '8px 16px', fontSize: '12px', color: colores.gris, fontFamily: 'monospace' }}>{e.numero_serie}</td>
                          <td style={{ padding: '8px 16px', fontSize: '12px', color: colores.grisClaro }}>{e.fecha_instalacion}</td>
                          <td style={{ padding: '8px 16px' }}>
                            <button style={{ ...btnStyle('#f3f4f4'), color: colores.oscuro, fontSize: '11px' }} onClick={() => alert('Navegar a cliente: ' + sucNombre)}>Ver en cliente</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  };

  // ============================================
  // MODAL — Historial de asignaciones
  // ============================================
  const renderHistorial = () => {
    if (!mostrarHistorial) return null;
    const h = herramientasData.find(x => x.id === mostrarHistorial);
    if (!h) return null;
    return (
      <>
        <div onClick={() => setMostrarHistorial(null)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998 }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '440px', zIndex: 9999, fontFamily: PP, boxShadow: '0 8px 30px rgba(0,0,0,0.2)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: colores.gris }}>Historial: {h.nombre}</div>
            <div onClick={() => setMostrarHistorial(null)} style={{ width: '28px', height: '28px', borderRadius: '50%', background: colores.fondo, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '13px', color: colores.grisClaro }}>{'✕'}</div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <BadgeFolio folio={h.folio} />
            <span style={{ marginLeft: '8px', fontSize: '12px', color: colores.grisClaro }}>{h.marca} {h.modelo}</span>
          </div>
          {h.historial_asignaciones.length === 0 ? (
            <div style={{ fontSize: '13px', color: colores.grisClaro, textAlign: 'center', padding: '20px 0' }}>Sin historial de asignaciones</div>
          ) : (
            <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {h.historial_asignaciones.map((a, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f0f0f0', alignItems: 'center' }}>
                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: a.hasta ? colores.grisClaro : colores.verde, flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: colores.gris }}>{a.responsable}</div>
                    <div style={{ fontSize: '11px', color: colores.grisClaro }}>{a.desde} {a.hasta ? ' al ' + a.hasta : ' (actual)'}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </>
    );
  };

  // ============================================
  // MODAL — Resultado de escaneo
  // ============================================
  const renderResultadoEscaneo = () => {
    if (!resultadoEscaneo) return null;
    const { tipo, item, codigo } = resultadoEscaneo;
    return (
      <>
        <div onClick={() => { setResultadoEscaneo(null); setFormNuevo(null); setErrorSerie(''); }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998 }} />
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#fff', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '500px', zIndex: 9999, fontFamily: PP, boxShadow: '0 8px 30px rgba(0,0,0,0.2)', maxHeight: 'calc(var(--beep-vh) * 80)', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', color: colores.gris }}>Resultado del escaneo</div>
            <div onClick={() => { setResultadoEscaneo(null); setFormNuevo(null); setErrorSerie(''); }} style={{ width: '28px', height: '28px', borderRadius: '50%', background: colores.fondo, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '13px', color: colores.grisClaro }}>{'✕'}</div>
          </div>

          {tipo !== 'no_encontrado' ? (
            <div>
              <div style={{ background: '#e8faf5', borderRadius: '10px', padding: '14px', marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#1D9E75', fontWeight: '600', marginBottom: '6px' }}>Equipo encontrado en: {tipo === 'herramienta' ? 'Herramientas Beep' : tipo === 'equipo' ? 'Equipos para instalar' : 'Instalado en clientes'}</div>
              </div>
              <div style={{ display: 'grid', gap: '8px' }}>
                <div><span style={labelStyle}>Folio: </span><BadgeFolio folio={item.folio} /></div>
                <div><span style={labelStyle}>Nombre: </span><span style={valStyle}>{item.nombre}</span></div>
                <div><span style={labelStyle}>Marca / Modelo: </span><span style={valStyle}>{item.marca} {item.modelo}</span></div>
                <div><span style={labelStyle}>Serie: </span><span style={{ ...valStyle, fontFamily: 'monospace' }}>{item.numero_serie}</span></div>
                <div><span style={labelStyle}>Estatus: </span><BadgeEstatus estatus={item.estatus} mapa={tipo === 'herramienta' ? estatusHerramientaColor : estatusEquipoColor} /></div>
                {item.responsable && <div><span style={labelStyle}>Responsable: </span><span style={valStyle}>{item.responsable}</span></div>}
                {item.sucursal_id != null && <div><span style={labelStyle}>Sucursal: </span><span style={valStyle}>{(getSucursal(item.sucursal_id) || {}).nombre || '--'}</span></div>}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ background: '#fff3e8', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ fontSize: '12px', color: '#b55e1a', fontWeight: '600' }}>No se encontró equipo con serie: <span style={{ fontFamily: 'monospace' }}>{codigo}</span></div>
              </div>
              {formNuevo && (
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '700', color: colores.gris, marginBottom: '12px' }}>Registrar nuevo equipo</div>
                  <div style={{ display: 'grid', gap: '10px' }}>
                    <div>
                      <div style={labelStyle}>No. Serie</div>
                      <input value={formNuevo.numero_serie} onChange={e => handleNuevoSerieChange(e.target.value)} style={{ width: '100%', padding: '8px 10px', border: '1px solid ' + (errorSerie ? colores.rojo : '#d0d0d0'), borderRadius: '8px', fontSize: '13px', fontFamily: PP, boxSizing: 'border-box', outline: 'none' }} />
                      {errorSerie && <div style={{ fontSize: '11px', color: colores.rojo, marginTop: '4px', fontWeight: '500' }}>{errorSerie}</div>}
                    </div>
                    <div>
                      <div style={labelStyle}>Nombre</div>
                      <input value={formNuevo.nombre} onChange={e => setFormNuevo(p => ({ ...p, nombre: e.target.value }))} placeholder="Ej: Cámara Domo IP 4MP" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '8px', fontSize: '13px', fontFamily: PP, boxSizing: 'border-box', outline: 'none' }} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div>
                        <div style={labelStyle}>Marca</div>
                        <input value={formNuevo.marca} onChange={e => setFormNuevo(p => ({ ...p, marca: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '8px', fontSize: '13px', fontFamily: PP, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                      <div>
                        <div style={labelStyle}>Modelo</div>
                        <input value={formNuevo.modelo} onChange={e => setFormNuevo(p => ({ ...p, modelo: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '8px', fontSize: '13px', fontFamily: PP, boxSizing: 'border-box', outline: 'none' }} />
                      </div>
                    </div>
                    <div>
                      <div style={labelStyle}>Categoría</div>
                      <select value={formNuevo.categoria} onChange={e => setFormNuevo(p => ({ ...p, categoria: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: '8px', fontSize: '13px', fontFamily: PP, boxSizing: 'border-box', background: '#fff' }}>
                        {['Cámara', 'DVR', 'NVR', 'Switch', 'Lector', 'Monitor', 'Rack', 'Panel alarma', 'Sensor', 'Herramienta', 'Otro'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <button disabled={!!errorSerie || !formNuevo.nombre || !formNuevo.numero_serie} onClick={() => { alert('Equipo registrado: ' + formNuevo.nombre); setResultadoEscaneo(null); setFormNuevo(null); setErrorSerie(''); }} style={{ ...btnStyle(errorSerie ? '#ccc' : colores.oscuro), padding: '10px', marginTop: '4px', opacity: (errorSerie || !formNuevo.nombre) ? 0.5 : 1 }}>Registrar equipo</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </>
    );
  };

  // ============================================
  // RENDER PRINCIPAL
  // ============================================
  return (
    <div style={{ fontFamily: PP, color: colores.gris, padding: esMobile ? '16px' : '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontSize: esMobile ? '18px' : '22px', fontWeight: '700', color: colores.oscuro, margin: 0 }}>Inventario</h2>
          <p style={{ fontSize: '13px', color: colores.grisClaro, margin: '4px 0 0' }}>Herramientas, equipos y activos instalados</p>
        </div>
        <button onClick={() => setEscanerAbierto(true)} style={{ ...btnStyle(colores.oscuro), padding: '10px 18px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '16px' }}>{'📷'}</span> Escanear equipo
        </button>
      </div>

      {/* Estadísticas rápidas */}
      <div style={{ display: 'grid', gridTemplateColumns: esMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Herramientas', valor: herramientasData.length, color: colores.oscuro },
          { label: 'Disponibles', valor: contHerr.Disponible, color: '#1D9E75' },
          { label: 'Equipos en almacén', valor: contEquip['En almacén'], color: '#6d3fcc' },
          { label: 'Instalados', valor: equiposInstaladosData.length, color: colores.principal },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: '12px', padding: '14px 16px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize: '11px', color: colores.grisClaro, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: s.color, marginTop: '4px' }}>{s.valor}</div>
          </div>
        ))}
      </div>

      {/* Pestañas */}
      <BarraPestanas tabs={tabs} activo={pestana} onChange={setPestana} />

      {/* Contenido */}
      <div style={{ background: '#fff', borderRadius: '0 0 12px 12px', padding: esMobile ? '14px' : '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        {pestana === 'herramientas' && renderHerramientas()}
        {pestana === 'equipos' && renderEquipos()}
        {pestana === 'instalado' && renderInstalados()}
      </div>

      {/* Modales */}
      {renderHistorial()}
      {renderResultadoEscaneo()}
      <Escaner abierto={escanerAbierto} onCerrar={() => setEscanerAbierto(false)} onEscaneo={handleEscaneo} placeholder="Escanea o escribe el número de serie:" />
    </div>
  );
}

export default Inventario;
